import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiRequestError } from '../../lib/api';
import { CameraScanner } from '../../components/CameraScanner';
import { extractTicketId } from '../../lib/scanPayload';
import { Card, FormField, PageHeader, Select, Spinner } from '../../components/ui';

/** Persisted per-browser so repeated scans from the same device/tab show a stable identity in
 * `checkedInBy` - a fresh random id every page load would make "who scanned this" useless. */
function getDeviceId(): string {
  const KEY = 'loctopus_scanner_device_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

type ScanOutcome =
  | { kind: 'granted'; holderName: string | null; ticketTypeName: string | null }
  | {
      kind: 'preview';
      holderName: string | null;
      ticketTypeName: string | null;
      eventDate: string;
      daysUntilEvent: number;
    }
  | { kind: 'already_scanned'; holderName: string | null; ticketTypeName: string | null; message: string }
  | { kind: 'rejected'; message: string };

interface ScanRecord {
  outcome: ScanOutcome;
  at: string;
}

const MAX_RECENT_SCANS = 10;

/**
 * Owner's explicit instruction: ticket scanning must live INSIDE this app at
 * staff.bobeventsandentertainments.com, with the same login as Reports - not a separate app/URL.
 * Ported from apps/scanner (CameraScanner, scanPayload, the check-in call shape and its date-aware
 * result handling - see apps/client-api/src/routes/management/checkIn.ts's `not_event_day`
 * result), simplified to an online-only flow (no offline queue/IndexedDB) since this is reached
 * over the same network connection the rest of this dashboard needs anyway - that complexity is
 * apps/scanner's own reason to keep existing as a dedicated device app, not something this page
 * needs to duplicate.
 */
export function ScannerPage() {
  const deviceId = useRef(getDeviceId()).current;
  const [eventId, setEventId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [current, setCurrent] = useState<ScanOutcome | null>(null);
  const [recent, setRecent] = useState<ScanRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const processingRef = useRef(false);

  const eventsQuery = useQuery({
    queryKey: ['scanner-events'],
    queryFn: () => api.events.list({ limit: 100 }),
  });
  const sessionsQuery = useQuery({
    queryKey: ['scanner-sessions', eventId],
    queryFn: () => api.sessions.list(eventId),
    enabled: !!eventId,
  });

  // Auto-pick the only event/session when there's exactly one - most tenants (Bob included) run
  // one event at a time, so forcing a click through two near-empty dropdowns every shift is pure
  // friction for exactly the "without any hassle" ask.
  useEffect(() => {
    if (!eventId && eventsQuery.data?.data.length === 1) {
      setEventId(eventsQuery.data.data[0]!.id);
    }
  }, [eventId, eventsQuery.data]);
  useEffect(() => {
    if (eventId && !sessionId && sessionsQuery.data?.data.length === 1) {
      setSessionId(sessionsQuery.data.data[0]!.id);
    }
  }, [eventId, sessionId, sessionsQuery.data]);

  async function handleDecode(raw: string) {
    if (processingRef.current || current) return;
    processingRef.current = true;
    setBusy(true);
    try {
      const ticketId = extractTicketId(raw);
      const outcome = await resolveScan(ticketId, raw, sessionId, deviceId);
      setCurrent(outcome);
      setRecent((prev) => [{ outcome, at: new Date().toISOString() }, ...prev].slice(0, MAX_RECENT_SCANS));
    } finally {
      setBusy(false);
      processingRef.current = false;
    }
  }

  if (eventsQuery.isLoading) return <Spinner />;

  return (
    <div>
      <PageHeader title="Scanner" description="Scan a ticket's QR code to check it in at the door." />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Event">
            <Select
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value);
                setSessionId('');
              }}
            >
              <option value="">Select an event...</option>
              {eventsQuery.data?.data.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Session">
            <Select value={sessionId} onChange={(e) => setSessionId(e.target.value)} disabled={!eventId}>
              <option value="">Select a session...</option>
              {sessionsQuery.data?.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {new Date(s.startsAt).toLocaleString()}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
      </Card>

      {!sessionId ? (
        <p className="text-sm text-slate-500">Pick an event and session above to start scanning.</p>
      ) : (
        <div className="flex flex-col items-center gap-4">
          {!current && (
            <>
              <CameraScanner active={scanning} onDecode={handleDecode} />
              {!scanning && (
                <button
                  type="button"
                  onClick={() => setScanning(true)}
                  className="rounded-lg bg-brand-primary px-6 py-3 font-semibold text-white"
                >
                  Start scanning
                </button>
              )}
              {busy && <Spinner label="Checking ticket..." />}
            </>
          )}
          {current && (
            <ScanResultCard
              outcome={current}
              onDismiss={() => {
                setCurrent(null);
                setScanning(true);
              }}
            />
          )}
        </div>
      )}

      <RecentScans scans={recent} />
    </div>
  );
}

async function resolveScan(
  ticketId: string,
  rawPayload: string,
  sessionId: string,
  deviceId: string,
): Promise<ScanOutcome> {
  let ticket: { status: string; holderName: string | null; ticketTypeName: string | null; sessionId: string };
  try {
    const res = await api.tickets.get(ticketId);
    ticket = res.ticket;
  } catch (err) {
    return { kind: 'rejected', message: err instanceof Error ? err.message : 'Ticket not found.' };
  }

  if (ticket.sessionId !== sessionId) {
    return { kind: 'rejected', message: "This ticket belongs to a different session than the one selected." };
  }

  try {
    const res = await api.tickets.checkIn(ticketId, {
      sessionId,
      deviceId,
      scannedAt: new Date().toISOString(),
      // Only a real camera decode has a signature to verify - `rawPayload` IS `ticketId` itself
      // when a scan somehow bypassed the camera (shouldn't happen in this page today, but
      // `extractTicketId` can legitimately return its input unchanged), so sending it as-is in
      // that case just makes the server's signature check correctly fail closed instead of
      // silently skipping verification.
      qrPayload: rawPayload,
    });
    if (res.result === 'not_event_day') {
      return {
        kind: 'preview',
        holderName: ticket.holderName,
        ticketTypeName: ticket.ticketTypeName,
        eventDate: res.eventDate,
        daysUntilEvent: res.daysUntilEvent,
      };
    }
    return { kind: 'granted', holderName: ticket.holderName, ticketTypeName: ticket.ticketTypeName };
  } catch (err) {
    if (err instanceof ApiRequestError && err.code === 'conflict') {
      if (ticket.status === 'checked_in') {
        return {
          kind: 'already_scanned',
          holderName: ticket.holderName,
          ticketTypeName: ticket.ticketTypeName,
          message: err.message,
        };
      }
      return { kind: 'rejected', message: err.message };
    }
    // A session that logged in BEFORE the CSRF-token-in-body fix (see lib/api.ts's
    // readCsrfToken doc comment) never received a token to send at all - the raw server message
    // is accurate but not actionable to a non-technical reader. "Log out and back in" IS the fix
    // in that case (a fresh login response carries the token), so say that directly.
    if (err instanceof ApiRequestError && err.code === 'forbidden' && err.message.includes('X-CSRF-Token')) {
      return {
        kind: 'rejected',
        message: 'Your session needs to be refreshed - please log out and log back in, then try scanning again.',
      };
    }
    return { kind: 'rejected', message: err instanceof Error ? err.message : 'Check-in failed.' };
  }
}

function ScanResultCard({ outcome, onDismiss }: { outcome: ScanOutcome; onDismiss: () => void }) {
  const styles: Record<ScanOutcome['kind'], { border: string; bg: string; text: string }> = {
    granted: { border: 'border-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    preview: { border: 'border-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    already_scanned: { border: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
    rejected: { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  };
  const s = styles[outcome.kind];

  let title = 'Rejected';
  let holderName: string | null | undefined;
  let detail = '';
  if (outcome.kind === 'granted') {
    title = 'Access granted';
    holderName = outcome.holderName;
    detail = outcome.ticketTypeName ?? '';
  } else if (outcome.kind === 'preview') {
    title =
      outcome.daysUntilEvent > 0
        ? `Valid - event is in ${outcome.daysUntilEvent} day${outcome.daysUntilEvent === 1 ? '' : 's'}`
        : `Valid - event was ${Math.abs(outcome.daysUntilEvent)} day${Math.abs(outcome.daysUntilEvent) === 1 ? '' : 's'} ago`;
    holderName = outcome.holderName;
    detail = `Not admitted - check-in only applies on the event date (${outcome.eventDate}).`;
  } else if (outcome.kind === 'already_scanned') {
    title = 'Already scanned';
    holderName = outcome.holderName;
    detail = outcome.message;
  } else {
    detail = outcome.message;
  }

  return (
    <Card className={`w-full max-w-sm border-2 ${s.border} ${s.bg}`}>
      <p className={`text-sm font-semibold uppercase tracking-wide ${s.text}`}>{title}</p>
      {holderName && <p className="mt-1 text-xl font-bold text-slate-800">{holderName}</p>}
      {detail && <p className="mt-1 text-sm text-slate-600">{detail}</p>}
      <button
        type="button"
        onClick={onDismiss}
        className="mt-4 w-full rounded-lg bg-slate-800 py-2.5 font-semibold text-white"
      >
        Next scan
      </button>
    </Card>
  );
}

function RecentScans({ scans }: { scans: ScanRecord[] }) {
  if (scans.length === 0) return null;
  return (
    <Card className="mt-6" title="Recent scans">
      <ul className="divide-y divide-slate-100">
        {scans.map((scan, i) => {
          const dot =
            scan.outcome.kind === 'granted' || scan.outcome.kind === 'preview'
              ? 'bg-emerald-500'
              : scan.outcome.kind === 'already_scanned'
                ? 'bg-amber-500'
                : 'bg-red-500';
          const name =
            scan.outcome.kind === 'rejected' ? 'Unknown ticket' : (scan.outcome.holderName ?? 'Unknown ticket');
          const time = new Date(scan.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          return (
            <li key={`${scan.at}-${i}`} className="flex items-center gap-3 py-2">
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} aria-hidden />
              <span className="flex-1 truncate text-sm text-slate-700">{name}</span>
              <span className="shrink-0 text-xs text-slate-400">{time}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
